import { useEffect, useCallback } from 'react';
import { useSignalRConnection } from './useSignalRConnection';

/**
 * Hook that subscribes to SignalR for real-time attendance updates.
 * When the user clocks in/out, the attendance data is refreshed.
 *
 * @param userId - The user ID to subscribe for
 * @param onUserClocks - Callback when user clocks in/out
 */
export const useAttendanceSignalR = (
  userId: string | undefined,
  onUserClocks: () => void
) => {
  const { subscribe, subscribeToPersonalAttendancePool, isConnected } = useSignalRConnection();

  // Handler for user clocks events
  const handleUserClocks = useCallback((message: string) => {
    onUserClocks();
  }, [onUserClocks]);

  useEffect(() => {
    if (!userId) return;

    // Subscribe to the personal attendance pool
    subscribeToPersonalAttendancePool(userId).catch(err => {
      console.error('Failed to subscribe to personal attendance pool:', err);
    });

    // Subscribe to the UserClocks event
    const unsubscribe = subscribe('UserClocks', handleUserClocks);

    return () => {
      unsubscribe();
    };
  }, [userId, subscribe, subscribeToPersonalAttendancePool, handleUserClocks]);

  return { isConnected };
};

export default useAttendanceSignalR;
